import React, { useState, useEffect } from 'react';

import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

const { TextArea } = Input;

const CURRENCY_OPTIONS = ['TWD', 'USD', 'JPY', 'EUR', 'SGD', 'KRW', 'CNY', 'HKD'];

// Minimal shape consumed by useRecordMerchantSync — accepts both top-level
// merchant_id and nested merchant: { id } as returned by the BE show payload.
interface SupplierLoadedPayload {
  merchant_id?: string | null;
  merchant?: { id?: string | null; name?: string | null } | null;
  [key: string]: unknown;
}

interface SupplierEditProps {
  // service hook 是 .js，方法 (get/create/update/destroy) 簽章不嚴格；先 any 過渡。
  service: any;
  role?: 'admin' | 'operator';
  breadcrumbPrefix?: Array<string | { label: string }>;
  // Called by `loadSupplier` once the supplier is fetched. Operator wrapper uses
  // this to sync OperatorDataContext.currentMerchant to the record's merchant
  // before any merchant-scoped dropdown renders. Today there are no
  // merchant-scoped dropdowns on this form, but the prop is wired in advance to
  // future-proof against added fields (sales_channel / brand / shipping_type …).
  onSupplierLoaded?: (supplier: SupplierLoadedPayload) => void;
  // True while the wrapper is still switching context to the record's merchant.
  // Render-gated to avoid the form briefly showing the previous merchant's options.
  merchantDataLoading?: boolean;
}

export default function SupplierEdit({
  service,
  role = 'admin',
  breadcrumbPrefix = ['purchase_orders', 'purchase_orders.suppliers'],
  onSupplierLoaded,
  merchantDataLoading = false,
}: SupplierEditProps) {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [dataLoading, setDataLoading] = useState(!isNew);

  useEffect(() => {
    if (!isNew) {
      loadSupplier();
    }
  }, [id]);

  const loadSupplier = async () => {
    setDataLoading(true);
    try {
      const response = await service.get(id);
      if (response.success) {
        const supplier = response.data as SupplierLoadedPayload;
        // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant
        // 對齊到該 record 的 merchant（由 useRecordMerchantSync 處理）。shared
        // 本身保持 merchant-agnostic。
        onSupplierLoaded?.(supplier);
        form.setFieldsValue(supplier);
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.load_failed') });
        goBack();
      }
    } catch (error) {
      handleNetworkError(error, { t });
      goBack();
    } finally {
      setDataLoading(false);
    }
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response
  const submitValues = async (values: Record<string, unknown>) => {
    return isNew
      ? await service.create(values)
      : await service.update(id, values);
  };

  // 主要提交：成功後導向 show 頁
  const handleSubmit = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      const response = await submitValues(values);

      if (response.success) {
        message.success(t(isNew ? 'common.message.create_success' : 'common.message.update_success'));
        // 導向該筆記錄的 show 頁
        const recordId = response.data?.id || id;
        navigate(`/suppliers/${recordId}`, { replace: true });
      } else {
        handleApiError(response, {
          t,
          fallbackMessage: t(isNew ? 'common.error.create_failed' : 'common.error.update_failed'),
        });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const response = await submitValues(values);

        if (response.success) {
          message.success(t('common.message.create_success'));
          form.resetFields();
        } else {
          handleApiError(response, {
            t,
            fallbackMessage: t('common.error.create_failed'),
          });
        }
      } catch (error) {
        handleNetworkError(error, { t });
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  // Render-gate: sync 期間（dataLoading 抓 record；merchantDataLoading 切 context 到
  // record 的 merchant）整段 form 不能 mount，避免下拉清單先用 header merchant render。
  if (dataLoading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        basePath={`/${role}`}
        items={[...breadcrumbPrefix, {
          label: isNew ? t('common.button.create') : t('common.button.edit'),
        }]}
      />
      <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Space size="large" style={{ display: 'flex' }} align="start">
            <Form.Item
              label={t('models.supplier.name')}
              name="name"
              rules={[{ required: true, message: t('common.form.required') }]}
              style={{ flex: 1 }}
            >
              <Input placeholder={t('common.placeholder.please_enter')} />
            </Form.Item>

            <Form.Item label={t('models.supplier.code')} name="code" style={{ width: 220 }}>
              <Input placeholder={t('common.placeholder.please_enter')} />
            </Form.Item>
          </Space>

          <Space size="large" style={{ display: 'flex' }} align="start">
            <Form.Item label={t('models.supplier.contact_name')} name="contact_name" style={{ flex: 1 }}>
              <Input placeholder={t('common.placeholder.please_enter')} />
            </Form.Item>

            <Form.Item label={t('models.supplier.contact_phone')} name="contact_phone" style={{ flex: 1 }}>
              <Input placeholder={t('common.placeholder.please_enter')} />
            </Form.Item>

            <Form.Item label={t('models.supplier.contact_email')} name="contact_email" style={{ flex: 1 }}>
              <Input placeholder={t('common.placeholder.please_enter')} />
            </Form.Item>
          </Space>

          <Form.Item label={t('models.supplier.address')} name="address">
            <TextArea rows={3} placeholder={t('common.placeholder.please_enter')} />
          </Form.Item>

          <Space size="large" style={{ display: 'flex' }} align="start">
            <Form.Item label={t('models.supplier.default_currency')} name="default_currency" style={{ width: 160 }}>
              <Select
                placeholder={t('common.form.select')}
                allowClear
                options={CURRENCY_OPTIONS.map((c) => ({ label: c, value: c }))}
              />
            </Form.Item>

            <Form.Item
              label={t('models.supplier.tax_rate')}
              name="tax_rate"
              normalize={(val) => val != null ? val / 100 : val}
              getValueProps={(val) => ({ value: val != null ? (val as number) * 100 : val })}
              style={{ width: 160 }}
            >
              <InputNumber step={1} min={0} max={100} addonAfter="%" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label={t('models.supplier.payment_terms')} name="payment_terms" style={{ flex: 1 }}>
              <Input placeholder={t('common.placeholder.please_enter')} />
            </Form.Item>
          </Space>

          <Form.Item label={t('models.supplier.note')} name="note">
            <TextArea rows={3} placeholder={t('common.placeholder.please_enter')} />
          </Form.Item>

          <FloatingActionBarSpacer />
        </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
        disabled={savingAndAdding}
        extraButtons={isNew ? (
          <Button
            loading={savingAndAdding}
            disabled={loading}
            onClick={handleSaveAndAddAnother}
          >
            {t('common.button.save_and_add_another')}
          </Button>
        ) : null}
        submitText={isNew ? t('common.button.create') : t('common.button.save')}
      />
    </div>
  );
}
